/**
 * Component: FiltrosOtOptimizados
 * Spec: 003-optimized-ot-list
 * 
 * Panel optimizado de filtros para lista de OTs usando React Bootstrap
 * Incluye búsqueda asíncrona de usuarios y sedes
 */

import React, { useState, useEffect, useRef } from 'react';
import { Form, Row, Col, Button, Accordion, Card } from 'react-bootstrap';
import AsyncSearchSelect from '../commons/AsyncSearchSelect';
import PropTypes from 'prop-types';

/**
 * Debounce de 300ms para inputs de texto: evita disparar una petición
 * al backend por cada tecla (spec 003 lo dejaba pendiente) y evita que
 * una respuesta de un valor anterior llegue tarde y pise el resultado
 * del último texto escrito.
 */
const useDebouncedFilterInput = (externalValue, onChange, name, delay = 300) => {
  const [value, setValue] = useState(externalValue || '');
  const timeoutRef = useRef(null);

  useEffect(() => {
    setValue(externalValue || '');
  }, [externalValue]);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const handleChangeDebounced = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      onChange(name, newValue);
    }, delay);
  };

  return [value, handleChangeDebounced];
};

/**
 * Componente de filtros para lista de OTs
 * @param {Object} props
 * @param {Object} props.filters - Filtros actuales
 * @param {Function} props.onFilterChange - Callback para cambio individual de filtro
 * @param {Function} props.onResetFilters - Callback para resetear todos
 */
export const FiltrosOtOptimizados = ({ 
  filters = {}, 
  onFilterChange, 
  onResetFilters 
}) => {
  const [expanded, setExpanded] = useState(true);

  /**
   * Handler genérico para cambios en filtros
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    onFilterChange(name, value);
  };

  const [numeroOrdenValue, handleNumeroOrdenChange] = useDebouncedFilterInput(
    filters.numeroOrden, onFilterChange, 'numeroOrden'
  );
  const [ciudadValue, handleCiudadChange] = useDebouncedFilterInput(
    filters.ciudadCliente, onFilterChange, 'ciudadCliente'
  );

  /**
   * Estados disponibles para OTs
   */
  const estadosOt = [
    { value: '', label: 'Todos los estados' },
    { value: 'Solicitud', label: 'Solicitud' },
    { value: 'Programada', label: 'Programada' },
    { value: 'Abierta', label: 'Abierta' },
    { value: 'Lista para Cerrar', label: 'Lista para Cerrar' },
    { value: 'Cerrada', label: 'Cerrada' },
    { value: 'Pagada', label: 'Pagada' },
    { value: 'Entregada', label: 'Entregada' }
  ];

  /**
   * Roles de usuarios
   */
  const rolesUsuario = [
    { value: '', label: 'Todos los roles' },
    { value: 'TECNICO', label: 'Técnico' },
    { value: 'ADMIN', label: 'Administrador' },
    { value: 'COTIZADOR', label: 'Cotizador' },
    { value: 'RESPONSABLE', label: 'Responsable' }
  ];

  /**
   * Contar filtros activos
   */
  const countActiveFilters = () => {
    return Object.keys(filters).filter(key => 
      filters[key] !== '' && 
      filters[key] !== undefined && 
      filters[key] !== null
    ).length;
  };

  const activeCount = countActiveFilters();

  return (
    <Accordion defaultActiveKey={expanded ? '0' : null} className="mb-3">
      <Accordion.Item eventKey="0">
        <Accordion.Header onClick={() => setExpanded(!expanded)}>
          <strong>🔍 Filtros de Búsqueda</strong>
          {activeCount > 0 && (
            <span className="badge bg-primary ms-2">{activeCount} activos</span>
          )}
        </Accordion.Header>
        
        <Accordion.Body>
          <Form>
            <Row className="mb-3">
              {/* Número de Orden */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Número de Orden</Form.Label>
                  <Form.Control
                    type="text"
                    name="numeroOrden"
                    value={numeroOrdenValue}
                    onChange={handleNumeroOrdenChange}
                    placeholder="Ej: OT-2024-001"
                  />
                </Form.Group>
              </Col>

              {/* Estado */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Estado</Form.Label>
                  <Form.Select
                    name="estado"
                    value={filters.estado || ''}
                    onChange={handleChange}
                  >
                    {estadosOt.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Cliente (Async Search) */}
              <Col md={3}>
                <AsyncSearchSelect
                  type="usuario"
                  name="cliente"
                  label="Cliente"
                  value={filters.cliente || ''}
                  onChange={handleChange}
                  placeholder="Buscar cliente..."
                />
              </Col>

              {/* Técnico Responsable (Async Search) */}
              <Col md={3}>
                <AsyncSearchSelect
                  type="usuario"
                  name="responsable"
                  label="Técnico Responsable"
                  value={filters.responsable || ''}
                  onChange={handleChange}
                  placeholder="Buscar técnico..."
                  rol="Metrologo"
                />
              </Col>
            </Row>

            <Row className="mb-3">

              {/* Fecha Inicio */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Fecha Desde</Form.Label>
                  <Form.Control
                    type="date"
                    name="fechaDesde"
                    value={filters.fechaDesde || ''}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>

              {/* Fecha Fin */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Fecha Hasta</Form.Label>
                  <Form.Control
                    type="date"
                    name="fechaHasta"
                    value={filters.fechaHasta || ''}
                    onChange={handleChange}
                    disabled={!filters.fechaDesde}
                    min={filters.fechaDesde || ''}
                  />
                  {!filters.fechaDesde && (
                    <Form.Text className="text-muted">
                      Selecciona primero "Fecha Desde"
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              {/* Ciudad */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Ciudad</Form.Label>
                  <Form.Control
                    type="text"
                    name="ciudadCliente"
                    value={ciudadValue}
                    onChange={handleCiudadChange}
                    placeholder="Buscar por ciudad..."
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Botones de acción */}
            <Row>
              <Col className="d-flex justify-content-end gap-2">
                <Button 
                  variant="outline-secondary" 
                  onClick={onResetFilters}
                  disabled={activeCount === 0}
                >
                  🗑️ Limpiar Filtros
                </Button>
                <Button 
                  variant="primary" 
                  type="button"
                  disabled={activeCount === 0}
                >
                  ✓ {activeCount} filtro{activeCount !== 1 ? 's' : ''} aplicado{activeCount !== 1 ? 's' : ''}
                </Button>
              </Col>
            </Row>
          </Form>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

FiltrosOtOptimizados.propTypes = {
  filters: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired
};

export default FiltrosOtOptimizados;
